import React, { useState, useEffect } from "react";
import Card, { CardContent } from "./components/ui/Card";
import Input from "./components/ui/Input";
import Badge from "./components/ui/Badge";


const CLIENT_ID = "your-client-id"; // Twitch Client ID
const CLIENT_SECRET = "your-client-secret"; // Twitch Client Secret
const CHANNEL_NAME = "JessieTV"; // Twitch Channel Name

const ModDashboard = () => {
  const [chatMessages, setChatMessages] = useState([]);
  const [activeMods, setActiveMods] = useState([]);
  const [activeUsers, setActiveUsers] = useState([]);
  const [keywordFilter, setKeywordFilter] = useState("keyword");
  const [filteredMessages, setFilteredMessages] = useState([]);
  const [accessToken, setAccessToken] = useState(null);

  // Fetch Access Token from Twitch API
  useEffect(() => {
    const fetchAccessToken = async () => {
      try {
        const response = await fetch("https://id.twitch.tv/oauth2/token", {
          method: "POST",
          headers: { "Content-Type": "application/x-www-form-urlencoded" },
          body: new URLSearchParams({
            client_id: CLIENT_ID,
            client_secret: CLIENT_SECRET,
            grant_type: "client_credentials",
          }),
        });
        const data = await response.json();
        setAccessToken(data.access_token);
      } catch (error) {
        console.error("Error fetching access token:", error);
      }
    };

    fetchAccessToken();
  }, []);

  // Fetch Active Moderators and Users
  useEffect(() => {
    if (!accessToken) return;

    const fetchModerators = async () => {
      try {
        const userResponse = await fetch(
          `https://api.twitch.tv/helix/users?login=${CHANNEL_NAME}`,
          {
            headers: {
              Authorization: `Bearer ${accessToken}`,
              "Client-Id": CLIENT_ID,
            },
          }
        );
        const userData = await userResponse.json();
        const broadcasterId = userData.data[0].id;

        const modResponse = await fetch(
          `https://api.twitch.tv/helix/moderation/moderators?broadcaster_id=${broadcasterId}`,
          {
            headers: {
              Authorization: `Bearer ${accessToken}`,
              "Client-Id": CLIENT_ID,
            },
          }
        );
        const modData = await modResponse.json();
        setActiveMods(modData.data.map((mod) => mod.user_name));
      } catch (error) {
        console.error("Error fetching moderators:", error);
      }
    };

    fetchModerators();
  }, [accessToken]);

  // Connect to Twitch Chat via WebSocket (IRC)
  useEffect(() => {
    if (!accessToken) return;

    const socket = new WebSocket("wss://irc-ws.chat.twitch.tv");

    socket.onopen = () => {
      socket.send(`PASS oauth:${accessToken}`);
      socket.send(`NICK justinfan12345`); // Anonymous login
      socket.send(`JOIN #${CHANNEL_NAME}`);
    };

    socket.onmessage = (event) => {
      const message = event.data;
      if (message.includes("PRIVMSG")) {
        const parsedMessage = parseChatMessage(message);
        setChatMessages((prev) => [parsedMessage, ...prev]);

        // Track active users
        setActiveUsers((prev) => {
          const newSet = new Set(prev);
          newSet.add(parsedMessage.user);
          return Array.from(newSet);
        });

        // Filter messages by keyword
        if (parsedMessage.message.includes(keywordFilter)) {
          setFilteredMessages((prev) => [parsedMessage, ...prev]);
        }
      }
    };

    return () => socket.close();
  }, [accessToken, keywordFilter]);

  const parseChatMessage = (rawMessage) => {
    const userMatch = rawMessage.match(/:(\w+)!/);
    const messageMatch = rawMessage.match(/PRIVMSG #[^ ]+ :(.*)/);

    return {
      user: userMatch ? userMatch[1] : "Unknown",
      message: messageMatch ? messageMatch[1] : "",
      timestamp: new Date().toLocaleTimeString(),
    };
  };

  return (
    <div className="bg-gray-900 min-h-screen text-gold-500 p-6">
      <h1 className="text-3xl font-bold text-red-500 mb-4">JessieTV Mod Dashboard</h1>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {/* Active Moderators */}
        <Card className="bg-gray-800 border border-red-500 shadow-md">
          <CardContent>
            <h2 className="text-xl font-semibold text-gold-400">Aktive Moderatoren</h2>
            <ul className="mt-4">
              {activeMods.map((mod) => (
                <li key={mod} className="p-2 text-gray-100">
                  <Badge className="bg-red-500 text-white">{mod}</Badge>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>

        {/* Active Users */}
        <Card className="bg-gray-800 border border-gold-400 shadow-md">
          <CardContent>
            <h2 className="text-xl font-semibold text-gold-400">Aktive User</h2>
            <p className="text-sm mt-2 text-gray-300">Anzahl: {activeUsers.length}</p>
            <ul className="mt-4 h-32 overflow-auto">
              {activeUsers.map((user) => (
                <li key={user} className="p-2 text-gray-100">
                  {user}
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>

        {/* Filtered Messages */}
        <Card className="bg-gray-800 border border-red-500 shadow-md">
          <CardContent>
            <h2 className="text-xl font-semibold text-gold-400">Markierte Nachrichten</h2>
            <Input
              placeholder="Keyword eingeben"
              value={keywordFilter}
              onChange={(e) => setKeywordFilter(e.target.value)}
              className="mt-4 mb-6 bg-gray-700 text-white border-red-500"
            />
            <ul className="h-32 overflow-auto">
              {filteredMessages.map((msg, idx) => (
                <li key={idx} className="p-2 text-gray-100">
                  <span className="font-bold text-red-500">{msg.user}</span>: {msg.message}
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>

        {/* Chat Messages */}
        <Card className="col-span-1 md:col-span-2 lg:col-span-3 bg-gray-800 border border-gold-400 shadow-md">
          <CardContent>
            <h2 className="text-xl font-semibold text-gold-400">Chat Nachrichten</h2>
            <ul className="mt-4 h-64 overflow-auto bg-gray-700 p-4 rounded text-gray-100">
              {chatMessages.map((msg, idx) => (
                <li key={idx} className="mb-2">
                  <span className="font-bold text-red-500">[{msg.timestamp}] {msg.user}</span>: {msg.message}
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ModDashboard;
